import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClipboardCheck, Plus, Trash2, Save, GripVertical, Info, ShieldAlert, FileText } from "lucide-react";
import SwmsDialog from "@/components/SwmsDialog";

function uid() { return Math.random().toString(36).slice(2, 10); }

export default function RiskPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [swmsOpen, setSwmsOpen] = useState(false);

  useEffect(() => {
    api.get("/templates/risk").then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  const update = (id, val) => setItems((it) => it.map((x) => (x.id === id ? { ...x, hazard: val } : x)));
  const add = () => setItems((it) => [...it, { id: uid(), hazard: "" }]);
  const remove = (id) => setItems((it) => it.filter((x) => x.id !== id));

  const save = async () => {
    const clean = items.filter((x) => x.hazard.trim());
    setSaving(true);
    try {
      await api.put("/templates/risk", { items: clean });
      setItems(clean);
      toast.success("Risk template saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 fade-up max-w-4xl">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Assessment Template</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading flex items-center gap-2">
            <ClipboardCheck className="h-8 w-8 text-lime-500" /> Risk Assessment
          </h1>
          <p className="text-slate-500 mt-1">Manage the hazard template. This list is pulled into every job for a per-job risk assessment.</p>
        </div>
        <Button data-testid="risk-save-btn" onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white">
          <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save Template"}
        </Button>
      </div>

      <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 flex gap-3">
        <Info className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700">Add, edit or remove hazards below. Open any job → <strong>Risk Assessment</strong> to score each hazard (Likelihood × Consequence) and record mitigations for that specific job.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2">
        {items.map((it, i) => (
          <div key={it.id} data-testid={`risk-item-${i}`} className="flex items-center gap-2">
            <GripVertical className="h-4 w-4 text-slate-300 shrink-0" />
            <span className="text-xs font-bold text-slate-400 w-6 shrink-0">{i + 1}</span>
            <Input value={it.hazard} onChange={(e) => update(it.id, e.target.value)} placeholder="Hazard description" className="flex-1" />
            <Button size="icon" variant="outline" data-testid={`risk-remove-${i}`} onClick={() => remove(it.id)} className="text-red-600 border-red-200 hover:bg-red-50 shrink-0"><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        <Button variant="outline" onClick={add} data-testid="risk-add-btn" className="w-full border-dashed mt-2"><Plus className="h-4 w-4 mr-1.5" /> Add Hazard</Button>
      </div>
      <p className="text-xs text-slate-400">Scoring (applied per job): 1–3 Low · 4–7 Low-Med · 8–14 Medium · 15–25 High.</p>

      {/* SWMS master template */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 flex items-start gap-4" data-testid="swms-template-card">
        <span className="h-11 w-11 rounded-lg bg-lime-100 text-lime-700 flex items-center justify-center shrink-0"><ShieldAlert className="h-6 w-6" /></span>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-slate-900">Safe Work Method Statement (SWMS) template</h2>
          <p className="text-sm text-slate-500 mt-0.5">A generic drone SWMS you can complete on any job. Assign it to a job to auto-fill the safety details from the job, then export a branded PDF for staff and third parties.</p>
          <Button onClick={() => setSwmsOpen(true)} data-testid="edit-swms-template-btn" className="mt-3 bg-slate-900 hover:bg-slate-800 text-white" disabled={!isAdmin}>
            <FileText className="h-4 w-4 mr-1.5" /> {isAdmin ? "Edit SWMS template" : "Admin only"}
          </Button>
        </div>
      </div>

      <SwmsDialog open={swmsOpen} onOpenChange={setSwmsOpen} mode="template" />
    </div>
  );
}
